import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { generateTrainingQuestion } from '../../lib/api';
import { manageTrainingQuestions, manageContents, manageModels } from '../../lib/api';
import {
  Brain,
  Plus,
  Edit, 
  Trash2, 
  Save, 
  X, 
  Search, 
  Filter,
  Wand2,
  FileText,
  Calendar,
  Tag as TagIcon,
  Eye
} from 'lucide-react';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';

interface TrainingQuestion {
  id: string;
  title: string;
  content: string;
  content_id: string;
  thinking_type_tags: string[];
  ai_reference_answer: string;
  related_model_ids: string[];
  topic_tags: string[];
  status: 'draft' | 'published';
  created_at: string;
  contents?: {
    name: string;
    content_data: string;
  };
}

interface Content {
  id: string;
  name: string;
  content_data: string;
  topic_tags: string[];
  training_type_tags: string[];
}

interface Model {
  id: string;
  name: string;
}

interface QuestionFormData {
  title: string;
  content: string;
  content_id: string;
  thinking_type_tags: string[];
  ai_reference_answer: string;
  related_model_ids: string[];
  topic_tags: string[];
}

const TrainingQuestionManagement: React.FC = () => {
  const [questions, setQuestions] = useState<TrainingQuestion[]>([]);
  const [contents, setContents] = useState<Content[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<TrainingQuestion | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedThinkingTag, setSelectedThinkingTag] = useState<string>('');
  const [generating, setGenerating] = useState(false);
  const [viewingQuestion, setViewingQuestion] = useState<TrainingQuestion | null>(null);
  const [formData, setFormData] = useState<QuestionFormData>({
    title: '',
    content: '',
    content_id: '',
    thinking_type_tags: [],
    ai_reference_answer: '',
    related_model_ids: [],
    topic_tags: [],
  });

  useEffect(() => {
    fetchQuestions();
    fetchContents();
    fetchModels();
  }, []);

  const fetchQuestions = async () => {
    try {
      // First, get the training questions
      const questionsResponse = await manageTrainingQuestions('list');
      
      if (!questionsResponse.success) {
        throw new Error(questionsResponse.error || '获取训练题目失败');
      }

      const questionsData = questionsResponse.success && questionsResponse.data ?
        ((questionsResponse.data as any).questions || []) : [];

      // Then, get the content data for each question
      const contentsResponse = await manageContents('list');
      const contentsData = contentsResponse.success && contentsResponse.data ?
        ((contentsResponse.data as any).contents || []) : [];

      const questionsWithContent = questionsData.map((question: any) => {
        if (!question.content_id) {
          return question;
        }

        const contentData = contentsData.find((c: any) => c.id === question.content_id);
        
        if (!contentData) {
          console.warn(`Failed to find content for question ${question.id}`);
          return question;
        }

        return {
            ...question,
            contents: contentData,
          };
        });

      setQuestions(questionsWithContent);
    } catch (error) {
      console.error('获取训练题目失败:', error);
      toast.error('获取训练题目失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchContents = async () => {
    try {
      const response = await manageContents('list');
      
      if (response.success && response.data) {
        // 只显示已发布的内容
        const allContents = (response.data as any).contents || [];
        const publishedContents = allContents.filter((c: any) => c.status === 'published');
        setContents(publishedContents);
      } else {
        throw new Error(response.error || '获取内容失败');
      }
    } catch (error) {
      console.error('获取内容失败:', error);
    }
  };

  const fetchModels = async () => {
    try {
      const response = await manageModels('list');
      
      if (response.success && response.data) {
        // 只显示已发布的模型
        const allModels = (response.data as any).models || [];
        const publishedModels = allModels.filter((m: any) => m.status === 'published');
        setModels(publishedModels);
      } else {
        throw new Error(response.error || '获取模型失败');
      }
    } catch (error) {
      console.error('获取模型失败:', error);
    }
  };

  const generateQuestion = async () => {
    if (!formData.content_id) {
      toast.error('请先选择内容');
      return;
    }

    const selectedContent = contents.find(c => c.id === formData.content_id);
    if (!selectedContent) {
      toast.error('选择的内容不存在');
      return;
    }

    setGenerating(true);
    try {
      const modelNames = models.map(m => m.name);
      const response = await generateTrainingQuestion(
        selectedContent.content_data,
        selectedContent.name,
        modelNames,
        selectedContent.topic_tags,
        selectedContent.training_type_tags
      );

      console.log('AI生成响应:', response);

      // 获取推荐模型的ID
      const recommendedModelIds = response.recommendedModels 
        ? models.filter(m => response.recommendedModels.includes(m.name)).map(m => m.id)
        : [];

      setFormData({
        ...formData,
        title: response.title || `深度思考：基于《${selectedContent.name}》的思维训练`,
        content: response.content || '请基于提供的学习材料，运用相关思维模型进行深度分析和思考。',
        ai_reference_answer: response.referenceAnswer || '',
        topic_tags: response.topicTags && response.topicTags.length > 0 ? response.topicTags : selectedContent.topic_tags,
        thinking_type_tags: response.thinkingTypeTags && response.thinkingTypeTags.length > 0 ? response.thinkingTypeTags : selectedContent.training_type_tags,
        related_model_ids: recommendedModelIds,
      });

      toast.success('AI生成题目成功！请检查并调整生成的内容。');
    } catch (error) {
      console.error('生成题目失败:', error);
      toast.error('生成题目失败，请检查网络连接或稍后重试');
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('请输入题目标题');
      return;
    }

    if (!formData.content_id) {
      toast.error('请选择关联内容');
      return;
    }

    try {
      const questionData = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        content_id: formData.content_id,
        thinking_type_tags: formData.thinking_type_tags,
        ai_reference_answer: formData.ai_reference_answer.trim(),
        related_model_ids: formData.related_model_ids,
        topic_tags: formData.topic_tags,
      };

      if (editingQuestion) {
        // 更新题目
        const response = await manageTrainingQuestions('update', {
          id: editingQuestion.id,
          ...questionData
        });

        if (!response.success) {
          throw new Error(response.error || '更新题目失败');
        }
        toast.success('题目更新成功！');
      } else {
        // 创建新题目
        const response = await manageTrainingQuestions('create', questionData);

        if (!response.success) {
          throw new Error(response.error || '创建题目失败');
        }
        toast.success('题目创建成功！');
      }

      resetForm();
      fetchQuestions();
    } catch (error: any) {
      console.error('保存题目失败:', error);
      toast.error('保存题目失败');
    }
  };

  const handleEdit = (question: TrainingQuestion) => {
    setEditingQuestion(question);
    setFormData({
      title: question.title,
      content: question.content,
      content_id: question.content_id,
      thinking_type_tags: question.thinking_type_tags,
      ai_reference_answer: question.ai_reference_answer,
      related_model_ids: question.related_model_ids,
      topic_tags: question.topic_tags,
    });
    setShowForm(true);
  };

  const handleDelete = async (questionId: string) => {
    if (!confirm('确定要删除这个题目吗？')) return;

    try {
      const { error } = await supabase
        .from('training_questions')
        .delete()
        .eq('id', questionId);

      if (error) throw error;
      toast.success('题目删除成功！');
      fetchQuestions();
    } catch (error) {
      console.error('删除题目失败:', error);
      toast.error('删除题目失败');
    }
  };

  const handlePublish = async (questionId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    
    try {
      const { error } = await supabase
        .from('training_questions')
        .update({ status: newStatus })
        .eq('id', questionId);

      if (error) throw error;
      toast.success(`题目${newStatus === 'published' ? '发布' : '取消发布'}成功！`);
      fetchQuestions();
    } catch (error) {
      console.error('更新状态失败:', error);
      toast.error('更新状态失败');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      content_id: '',
      thinking_type_tags: [],
      ai_reference_answer: '',
      related_model_ids: [],
      topic_tags: [],
    });
    setEditingQuestion(null);
    setShowForm(false);
  };

  const handleTagChange = (tagType: 'thinking_type_tags' | 'topic_tags', tagName: string) => {
    const currentTags = formData[tagType];
    const newTags = currentTags.includes(tagName)
      ? currentTags.filter(tag => tag !== tagName)
      : [...currentTags, tagName];
    
    setFormData({ ...formData, [tagType]: newTags });
  };

  const handleModelChange = (modelId: string) => {
    const newModels = formData.related_model_ids.includes(modelId)
      ? formData.related_model_ids.filter(id => id !== modelId)
      : [...formData.related_model_ids, modelId];
    
    setFormData({ ...formData, related_model_ids: newModels });
  };

  const filteredQuestions = questions.filter(question => {
    const matchesSearch = question.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = selectedStatus === '' || question.status === selectedStatus;
    const matchesThinkingTag = selectedThinkingTag === '' || question.thinking_type_tags.includes(selectedThinkingTag);
    return matchesSearch && matchesStatus && matchesThinkingTag;
  });

  const allThinkingTags = Array.from(new Set(questions.flatMap(q => q.thinking_type_tags)));
  const allTopicTags = Array.from(new Set(questions.flatMap(q => q.topic_tags)));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
        {/* Header Actions */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="搜索题目..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-64"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">所有状态</option>
                <option value="draft">草稿</option>
                <option value="published">已发布</option>
              </select>
              <select
                value={selectedThinkingTag}
                onChange={(e) => setSelectedThinkingTag(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">思维类型</option>
                {allThinkingTags.map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            </div>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5 mr-2" />
            新建题目
          </button>
        </div>

        {/* Question Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {editingQuestion ? '编辑题目' : '新建题目'}
                  </h2>
                  <button
                    onClick={resetForm}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* 关联内容选择 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      关联内容 *
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={formData.content_id}
                        onChange={(e) => setFormData({ ...formData, content_id: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="">选择内容</option>
                        {contents.map(content => (
                          <option key={content.id} value={content.id}>
                            {content.name} 
                            {content.topic_tags.length > 0 && ` (${content.topic_tags.slice(0, 2).join(', ')})`}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={generateQuestion}
                        disabled={!formData.content_id || generating}
                        className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                      >
                        <Wand2 className="w-4 h-4 mr-2" />
                        {generating ? 'AI生成中...' : 'AI生成'}
                      </button>
                    </div>
                    {formData.content_id && (
                      <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-800">
                          💡 点击"AI生成"将基于选择的内容自动生成：
                          <br />• 有吸引力的题目标题
                          <br />• 具有挑战性的题目内容
                          <br />• 详细的参考答案
                          <br />• 相关的思维模型推荐
                          <br />• 匹配的标签分类
                          <br />生成后您可以根据需要进行调整。
                        </p>
                      </div>
                    )}
                  </div>

                  {/* 题目标题 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      题目标题 *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="请输入题目标题"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      例如：深度思考：如何运用批判性思维分析复杂问题
                    </p>
                  </div>

                  {/* 题目内容 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      题目内容 *
                    </label>
                    <textarea
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                      placeholder="详细描述题目要求和背景信息，让用户清楚知道要思考什么问题..."
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      这是用户在训练时看到的题目主体内容，应该包含具体的问题描述和思考要求
                    </p>
                  </div>

                  {/* 参考答案 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      参考答案
                    </label>
                    <textarea
                      value={formData.ai_reference_answer}
                      onChange={(e) => setFormData({ ...formData, ai_reference_answer: e.target.value })}
                      className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                      placeholder="输入参考答案..."
                    />
                  </div>

                  {/* 标签和模型选择 */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* 主题标签 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        主题标签
                      </label>
                      <div className="border border-gray-300 rounded-lg p-3 max-h-32 overflow-y-auto">
                        {allTopicTags.map(tag => (
                          <label key={tag} className="flex items-center mb-2">
                            <input
                              type="checkbox"
                              checked={formData.topic_tags.includes(tag)}
                              onChange={() => handleTagChange('topic_tags', tag)}
                              className="mr-2"
                            />
                            <span className="text-sm">{tag}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* 思维类型标签 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        思维类型标签
                      </label>
                      <div className="border border-gray-300 rounded-lg p-3 max-h-32 overflow-y-auto">
                        {allThinkingTags.map(tag => (
                          <label key={tag} className="flex items-center mb-2">
                            <input
                              type="checkbox"
                              checked={formData.thinking_type_tags.includes(tag)}
                              onChange={() => handleTagChange('thinking_type_tags', tag)}
                              className="mr-2"
                            />
                            <span className="text-sm">{tag}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* 关联模型 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      关联思维模型
                    </label>
                    <div className="border border-gray-300 rounded-lg p-3 max-h-32 overflow-y-auto">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {models.map(model => (
                          <label key={model.id} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData.related_model_ids.includes(model.id)}
                              onChange={() => handleModelChange(model.id)}
                              className="mr-2"
                            />
                            <span className="text-sm">{model.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      取消
                    </button>
                    <button
                      type="submit"
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {editingQuestion ? '更新' : '创建'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Questions List */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              题目列表 ({filteredQuestions.length})
            </h2>
          </div>

          {filteredQuestions.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {filteredQuestions.map((question) => (
                <div key={question.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-3 mb-2">
                        <Brain className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-medium text-gray-900">
                            {question.title}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {question.content}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            关联内容: {question.contents?.name}
                          </p>
                        </div>
                      </div>

                      {/* 标签显示 */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                          question.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {question.status === 'published' ? '已发布' : '草稿'}
                        </span>
                        {question.thinking_type_tags.map((tag, index) => (
                          <span key={index} className="inline-block px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">
                            思维: {tag}
                          </span>
                        ))}
                        {question.topic_tags.map((tag, index) => (
                          <span key={index} className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                            主题: {tag}
                          </span>
                        ))}
                      </div>

                      {/* 参考答案预览 */}

                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(question.created_at).toLocaleString()}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePublish(question.id, question.status)}
                        className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                          question.status === 'published'
                            ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                            : 'bg-green-100 text-green-800 hover:bg-green-200'
                        }`}
                      >
                        {question.status === 'published' ? '取消发布' : '发布'}
                      </button>
                      <button
                        onClick={() => setViewingQuestion(question)}
                        className="p-2 text-gray-400 hover:text-green-600 transition-colors"
                        title="查看详情"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(question)}
                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                        title="编辑"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(question.id)}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                        title="删除"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <Brain className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {searchTerm || selectedStatus || selectedThinkingTag ? '没有找到匹配的题目' : '暂无训练题目'}
              </h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || selectedStatus || selectedThinkingTag ? '尝试调整搜索条件' : '开始创建第一个训练题目'}
              </p>
              {!searchTerm && !selectedStatus && !selectedThinkingTag && (
                <button
                  onClick={() => setShowForm(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  创建题目
                </button>
              )}
            </div>
          )}
        </div>

        {/* Question View Modal */}
        {viewingQuestion && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    题目详情
                  </h2>
                  <button
                    onClick={() => setViewingQuestion(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* 题目标题 */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">题目标题</h3>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-gray-800">{viewingQuestion.title}</p>
                    </div>
                  </div>

                  {/* 关联内容 */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">关联内容</h3>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-blue-800">{viewingQuestion.contents?.name}</p>
                    </div>
                  </div>

                  {/* 题目内容 */}
                  {viewingQuestion.content && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">题目内容</h3>
                      <div className="p-4 bg-gray-50 rounded-lg prose max-w-none">
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => <p className="mb-3 last:mb-0 text-gray-700">{children}</p>,
                            ul: ({ children }) => <ul className="list-disc list-inside mb-3 space-y-1 text-gray-700">{children}</ul>,
                            ol: ({ children }) => <ol className="list-decimal list-inside mb-3 space-y-1 text-gray-700">{children}</ol>,
                            li: ({ children }) => <li className="text-gray-700">{children}</li>,
                            h1: ({ children }) => <h1 className="text-xl font-bold mb-3 text-gray-900">{children}</h1>,
                            h2: ({ children }) => <h2 className="text-lg font-bold mb-3 text-gray-900">{children}</h2>,
                            h3: ({ children }) => <h3 className="text-base font-bold mb-2 text-gray-900">{children}</h3>,
                            strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
                            em: ({ children }) => <em className="italic text-gray-700">{children}</em>,
                            code: ({ children }) => <code className="bg-gray-200 px-2 py-1 rounded text-sm font-mono text-gray-800">{children}</code>,
                            pre: ({ children }) => <pre className="bg-gray-200 p-3 rounded text-sm overflow-x-auto mb-3">{children}</pre>,
                            blockquote: ({ children }) => <blockquote className="border-l-4 border-gray-300 pl-4 italic mb-3 text-gray-600">{children}</blockquote>,
                          }}
                        >
                          {viewingQuestion.content}
                        </ReactMarkdown>
                      </div>
                    </div>
                  )}

                  {/* 参考答案 */}
                  {viewingQuestion.ai_reference_answer && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">参考答案</h3>
                      <div className="p-4 bg-green-50 rounded-lg prose max-w-none">
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => <p className="mb-3 last:mb-0 text-green-800">{children}</p>,
                            ul: ({ children }) => <ul className="list-disc list-inside mb-3 space-y-1 text-green-800">{children}</ul>,
                            ol: ({ children }) => <ol className="list-decimal list-inside mb-3 space-y-1 text-green-800">{children}</ol>,
                            li: ({ children }) => <li className="text-green-800">{children}</li>,
                            h1: ({ children }) => <h1 className="text-xl font-bold mb-3 text-green-900">{children}</h1>,
                            h2: ({ children }) => <h2 className="text-lg font-bold mb-3 text-green-900">{children}</h2>,
                            h3: ({ children }) => <h3 className="text-base font-bold mb-2 text-green-900">{children}</h3>,
                            strong: ({ children }) => <strong className="font-semibold text-green-900">{children}</strong>,
                            em: ({ children }) => <em className="italic text-green-800">{children}</em>,
                            code: ({ children }) => <code className="bg-green-200 px-2 py-1 rounded text-sm font-mono text-green-900">{children}</code>,
                            pre: ({ children }) => <pre className="bg-green-200 p-3 rounded text-sm overflow-x-auto mb-3">{children}</pre>,
                            blockquote: ({ children }) => <blockquote className="border-l-4 border-green-400 pl-4 italic mb-3 text-green-700">{children}</blockquote>,
                          }}
                        >
                          {viewingQuestion.ai_reference_answer}
                        </ReactMarkdown>
                      </div>
                    </div>
                  )}

                  {/* 标签信息 */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">标签信息</h3>
                    <div className="space-y-3">
                      {/* 状态 */}
                      <div>
                        <span className="text-sm font-medium text-gray-600 mr-2">状态:</span>
                        <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                          viewingQuestion.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {viewingQuestion.status === 'published' ? '已发布' : '草稿'}
                        </span>
                      </div>

                      {/* 主题标签 */}
                      {viewingQuestion.topic_tags.length > 0 && (
                        <div>
                          <span className="text-sm font-medium text-gray-600 mr-2">主题标签:</span>
                          <div className="inline-flex flex-wrap gap-1">
                            {viewingQuestion.topic_tags.map((tag, index) => (
                              <span key={index} className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* 思维类型标签 */}
                      {viewingQuestion.thinking_type_tags.length > 0 && (
                        <div>
                          <span className="text-sm font-medium text-gray-600 mr-2">思维类型:</span>
                          <div className="inline-flex flex-wrap gap-1">
                            {viewingQuestion.thinking_type_tags.map((tag, index) => (
                              <span key={index} className="inline-block px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* 创建时间 */}
                      <div>
                        <span className="text-sm font-medium text-gray-600 mr-2">创建时间:</span>
                        <span className="text-sm text-gray-700">
                          {new Date(viewingQuestion.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
                  <button
                    onClick={() => setViewingQuestion(null)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    关闭
                  </button>
                  <button
                    onClick={() => {
                      handleEdit(viewingQuestion);
                      setViewingQuestion(null);
                    }}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    编辑题目
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
  );
};

export default TrainingQuestionManagement;